import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Copy, Download, Mail, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiErrorText } from '@/lib/api-error'
import type { EinrichtungStart, ZweiFaktorVerfahren } from '@/lib/auth.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring'

/**
 * AP-38-E — **Die Zwei-Faktor-Anmeldung einrichten.** Drei Schritte:
 * Verfahren wählen → ersten Code eingeben → Wiederherstellungscodes sichern.
 *
 * Dieselbe Komponente für beide Wege: bei der Anmeldung mit Ticket (Pflicht)
 * und aus dem Profil. Was die Schritte am Server tun, geben die Aufrufer mit —
 * `starte` und `bestaetige`; die Komponente kennt weder Ticket noch Token.
 */
export function ZweiFaktorEinrichtung({
  starte,
  bestaetige,
  mitGeraet = false,
  onFertig,
  onAbbrechen,
}: {
  starte: (verfahren: ZweiFaktorVerfahren) => Promise<EinrichtungStart>
  /** Liefert die Wiederherstellungscodes. */
  bestaetige: (code: string, geraetMerken: boolean) => Promise<string[]>
  /** Bei der Anmeldung: „Dieses Gerät merken" anbieten. */
  mitGeraet?: boolean
  onFertig: () => void
  onAbbrechen?: () => void
}) {
  const { t } = useTranslation()
  const [schritt, setSchritt] = useState<'wahl' | 'code' | 'codes'>('wahl')
  const [verfahren, setVerfahren] = useState<ZweiFaktorVerfahren>('TOTP')
  const [start, setStart] = useState<EinrichtungStart | null>(null)
  const [code, setCode] = useState('')
  // Vorbelegt, wo es angeboten wird (Nachtrag H) — im Profil gibt es kein Gerät zu merken.
  const [merken, setMerken] = useState(mitGeraet)
  const [codes, setCodes] = useState<string[]>([])
  const [gesichert, setGesichert] = useState(false)
  const [kopiert, setKopiert] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const weiter = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      setStart(await starte(verfahren))
      setSchritt('code')
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    } finally {
      setLaeuft(false)
    }
  }

  const abschliessen = async (e: React.FormEvent) => {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      setCodes(await bestaetige(code.trim(), merken))
      setSchritt('codes')
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
    } finally {
      setLaeuft(false)
    }
  }

  const alsText = () => codes.join('\n')
  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(alsText())
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2000)
    } catch {
      // Ohne Zwischenablage bleibt das Herunterladen.
    }
  }
  const herunterladen = () => {
    const url = URL.createObjectURL(new Blob([`Symphenia — ${t('zweiFaktor.codesTitel')}\n\n${alsText()}\n`], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'symphenia-wiederherstellungscodes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      {fehler && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      )}

      {schritt === 'wahl' && (
        <>
          <p className="text-sm font-medium text-foreground">{t('zweiFaktor.verfahrenWaehlen')}</p>
          <div className="space-y-2">
            {(
              [
                { v: 'TOTP', icon: Smartphone, titel: t('zweiFaktor.verfahrenApp'), hinweis: t('zweiFaktor.verfahrenAppHinweis') },
                { v: 'EMAIL', icon: Mail, titel: t('zweiFaktor.verfahrenEmail'), hinweis: t('zweiFaktor.verfahrenEmailHinweis') },
              ] as const
            ).map(({ v, icon: Icon, titel, hinweis }) => (
              <label
                key={v}
                className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${
                  verfahren === v ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <input
                  type="radio"
                  name="verfahren"
                  className="mt-1 accent-[hsl(var(--primary))]"
                  checked={verfahren === v}
                  onChange={() => setVerfahren(v)}
                />
                <Icon size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block font-medium text-foreground">{titel}</span>
                  <span className="block text-muted-foreground">{hinweis}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            {onAbbrechen && (
              <Button type="button" variant="outline" onClick={onAbbrechen} disabled={laeuft}>
                {t('common.cancel')}
              </Button>
            )}
            <Button type="button" onClick={() => void weiter()} disabled={laeuft}>
              {t('zweiFaktor.weiter')}
            </Button>
          </div>
        </>
      )}

      {schritt === 'code' && start && (
        <form onSubmit={abschliessen} className="space-y-4">
          {start.verfahren === 'TOTP' && start.otpauthUrl ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('zweiFaktor.qrHinweis')}</p>
              <div className="flex justify-center rounded-md bg-white p-3">
                <QRCodeSVG value={start.otpauthUrl} size={176} />
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">{t('zweiFaktor.schluessel')}: </span>
                <code className="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                  {start.geheimnis?.replace(/(.{4})/g, '$1 ').trim()}
                </code>
              </div>
              <a href={start.otpauthUrl} className="inline-block text-sm text-primary hover:underline sm:hidden">
                {t('zweiFaktor.appOeffnen')}
              </a>
              <p className="text-sm text-foreground">{t('zweiFaktor.ersterCode')}</p>
            </div>
          ) : (
            <p className="text-sm text-foreground">{t('zweiFaktor.mailGesendet', { email: start.email ?? '' })}</p>
          )}
          <input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            aria-label={t('zweiFaktor.code')}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={`${eingabe} text-center font-mono text-lg tracking-[0.4em]`}
          />
          {mitGeraet && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={merken} onChange={(e) => setMerken(e.target.checked)} />
              <span>
                <span className="block text-foreground">{t('zweiFaktor.geraetMerken')}</span>
                <span className="block text-xs text-muted-foreground">{t('zweiFaktor.geraetMerkenHinweis')}</span>
              </span>
            </label>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setSchritt('wahl')} disabled={laeuft}>
              {t('zweiFaktor.zurueckSchritt')}
            </Button>
            <Button type="submit" disabled={laeuft || code.replace(/\s/g, '').length < 6}>
              {t('zweiFaktor.abschliessen')}
            </Button>
          </div>
        </form>
      )}

      {schritt === 'codes' && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground">{t('zweiFaktor.codesTitel')}</p>
          <p className="text-sm text-muted-foreground">{t('zweiFaktor.codesHinweis')}</p>
          <ul className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono text-sm" data-testid="wiederherstellungscodes">
            {codes.map((c) => (
              <li key={c} className="text-foreground">
                {c}
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => void kopieren()}>
              <Copy size={14} className="mr-1.5" />
              {kopiert ? t('zweiFaktor.codesKopiert') : t('zweiFaktor.codesKopieren')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={herunterladen}>
              <Download size={14} className="mr-1.5" />
              {t('zweiFaktor.codesHerunterladen')}
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={gesichert} onChange={(e) => setGesichert(e.target.checked)} />
            <span className="text-foreground">{t('zweiFaktor.codesGesichert')}</span>
          </label>
          <div className="flex justify-end">
            <Button type="button" onClick={onFertig} disabled={!gesichert}>
              {t('zweiFaktor.fertig')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
